import { useState } from 'react';

import { CheckBox, Pill, Spinner } from '@iwoca/orion';
import cn from 'classnames';

import styles from './PaymentCard.module.css';
import { Card } from '../../../../Buyer/components/Card/Card';
import { Divider } from '../../../../components/Divider/Divider';
import DownArrowIconSrc from '../../../../components/svg/DownArrowIcon.svg';
import { useCancelCapturedPaylink } from '../../hooks/useCancelCapturedPaylink';
import { PayLinkTableRow } from '../PayLinkTable/PayLinkTable.types';

export const PaymentCard = ({
  payLinkTableRow,
  isPayLinkChecked,
  clickCheckbox,
}: {
  payLinkTableRow: PayLinkTableRow;
  isPayLinkChecked: (pay_link_id: string) => boolean;
  clickCheckbox: (pay_link_id: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const onDetailsClick = () => setExpanded((prev) => !prev);

  if (payLinkTableRow.payLink.status === 'SCHEDULED_FOR_CAPTURE') {
    return <CapturedPaymentCard row={payLinkTableRow} />;
  }

  return (
    <Card className={styles.paymentCard}>
      <div className={styles.paymentCardInfo}>
        <PaymentCardInfo title="Ref" value={payLinkTableRow.reference}>
          {payLinkTableRow.payLink.source ===
            'iwocaPay_shopify_payment_app' && (
            <div className={styles.shopifyId}>
              ID: {payLinkTableRow?.shopify_payment_id}
            </div>
          )}
        </PaymentCardInfo>
        <PaymentCardInfo title="Amount" value={payLinkTableRow.amount} />
        <PaymentCardInfo title="Status" value={payLinkTableRow.status} />
        {expanded ? (
          <>
            <PaymentCardInfo
              title="Pay link type"
              value={<Pill>{payLinkTableRow.payLinkType}</Pill>}
            />
            <PaymentCardInfo
              title="Recipient"
              value={payLinkTableRow.recipient}
            />
          </>
        ) : null}
      </div>
      <Divider />
      <div className={styles.footer}>
        <CheckBox
          title={`Archive checkbox ${payLinkTableRow.id}`}
          checked={isPayLinkChecked(payLinkTableRow.id)}
          onChange={() => clickCheckbox(payLinkTableRow.id)}
          className={styles.checkbox}
        />
        <span className={styles.dateCreated}>
          {payLinkTableRow.dateCreated}
        </span>
        <button onClick={onDetailsClick} className={styles.detailsButton}>
          Details{' '}
          <img
            src={DownArrowIconSrc}
            alt=""
            className={expanded ? styles.pointArrowUp : undefined}
          />
        </button>
      </div>
    </Card>
  );
};

const CapturedPaymentCard = ({ row }: { row: PayLinkTableRow }) => {
  const [confirmationState, setConfirmationState] = useState<boolean>(false);
  const {
    cancelPayLink,
    isCancellingPayLink,
    errorCancellingPayLink,
    successCancellingPayLink,
  } = useCancelCapturedPaylink({ payLinkId: row.payLink.id });

  const CapturedPaymentCardButtons = () => {
    if (successCancellingPayLink) {
      return null;
    }

    if (isCancellingPayLink) {
      return (
        <div className={styles.cpSpinner}>
          <Spinner size="small" />
        </div>
      );
    }

    if (confirmationState) {
      return (
        <>
          <button
            className={styles.cpCancelOrder}
            onClick={() => cancelPayLink()}
          >
            {errorCancellingPayLink ? 'Try again' : 'Yes, cancel order'}
          </button>
          <button
            className={styles.cpDontCancelOrder}
            onClick={() => setConfirmationState(false)}
          >
            Don't cancel
          </button>
        </>
      );
    }

    return (
      <button
        className={styles.cpCancelOrder}
        onClick={() => setConfirmationState(true)}
      >
        Cancel Order
      </button>
    );
  };

  return (
    <Card
      className={cn(styles.capturedPaymentCard, {
        [styles.capturedPaymentCardCancelled]: successCancellingPayLink,
      })}
    >
      <div className={styles.cpDetails}>
        <div className={styles.cpCardHeader}>
          <div className={styles.cpRef}>{row.reference}</div>
          <div className={styles.cpAmount}>{row.amount}</div>
        </div>
        <p className={styles.cpRecipient}>{row.recipient}</p>
        <div className={styles.cpCardMid}>
          <div>
            {successCancellingPayLink ? (
              <>Cancelled</>
            ) : (
              <>
                <Pill variant="green">Pending funds</Pill>
                <p className={styles.cpToBePaid}>
                  To be paid on {/*// @ts-ignore*/}
                  {new Date(row.payLink.capture_by).toLocaleDateString('en-GB')}
                </p>
              </>
            )}
          </div>
          <div className={styles.cpCardFloatRight}>
            <div className={styles.cpPaylinkType}>{row.payLinkType}</div>
            <p className={styles.cpDate}>{row.dateCreated}</p>
          </div>
        </div>
      </div>
      <hr
        className={cn(styles.cpHr, {
          [styles.cpHrCancelled]: successCancellingPayLink,
        })}
      />
      <CapturedPaymentCardButtons />
    </Card>
  );
};

const PaymentCardInfo = ({
  title,
  value,
  children,
}: {
  title: string;
  value: string | React.ReactNode;
  children?: React.ReactNode;
}) => {
  return (
    <div className={styles.paymentCardInfoContent}>
      <strong className={styles.infoTitle}>{title}</strong>
      {value}
      {children}
    </div>
  );
};
